import { getCachedValue, setCachedValue } from './cacheService';
import { markCacheSeeded } from './cacheMeta';
import { referenceApi } from './reference';
import { rolesApi } from './rolesApi';

const DEFAULT_TTL_MS = 30 * 60 * 1000;

export const REFERENCE_CACHE_KEYS = {
  states: 'reference:states',
  lgas: (state) => `reference:lgas:${String(state ?? '').trim().toLowerCase()}`,
  wards: (state, lga) => `reference:wards:${String(state ?? '').trim().toLowerCase()}:${String(lga ?? '').trim().toLowerCase()}`,
  species: 'reference:species',
  breeds: 'reference:breeds',
  productionPurposes: 'reference:production-purposes',
  productionTypes: 'reference:production-types',
  ageClasses: 'reference:age-classes',
  productionSystems: 'reference:production-systems',
  countMethods: 'reference:count-methods',
  dataSources: 'reference:data-sources',
  validationStatuses: 'reference:validation-statuses',
  institutions: 'reference:institutions',
  facilityTypes: 'reference:facility-types',
  workforceActors: 'reference:workforce-actors',
  workforceDeployments: 'reference:workforce-deployments',
  markets: 'reference:markets',
  borderCrossings: 'reference:border-crossings',
  vetCertificates: 'reference:vet-certificates',
  animalRegistrations: 'reference:animal-registrations',
  outbreakRecords: 'reference:outbreak-records',
  vaccinationRecords: 'reference:vaccination-records',
  mortalityRecords: 'reference:mortality-records',
  conflictRecords: 'reference:conflict-records',
  workplans: 'reference:workplans',
  workplanActivities: 'reference:workplan-activities',
  activityReports: 'reference:activity-reports',
  supplementaryDatasets: 'reference:supplementary-datasets',
};

function normalizeResult(result, fallback = []) {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.items)) return result.items;
  if (Array.isArray(result?.results)) return result.results;
  return fallback;
}

export async function fetchReferenceData(cacheKey, loader, fallback = [], ttlMs = DEFAULT_TTL_MS) {
  const cached = getCachedValue(cacheKey);
  if (cached?.value !== undefined) return cached.value;

  try {
    const result = await loader();
    const normalized = normalizeResult(result, fallback);
    setCachedValue(cacheKey, normalized, ttlMs);
    markCacheSeeded({ source: cacheKey });
    return normalized;
  } catch {
    return fallback;
  }
}

export async function warmUpCache() {
  await Promise.allSettled([
    fetchReferenceData(REFERENCE_CACHE_KEYS.states, () => referenceApi.states(), []),
    fetchReferenceData(REFERENCE_CACHE_KEYS.validationStatuses, () => referenceApi.validationStatuses(), []),
    fetchReferenceData(REFERENCE_CACHE_KEYS.productionSystems, () => referenceApi.productionSystems(), []),
    fetchReferenceData(REFERENCE_CACHE_KEYS.countMethods, () => referenceApi.countMethods(), []),
    fetchReferenceData(REFERENCE_CACHE_KEYS.dataSources, () => referenceApi.dataSources(), []),
    fetchReferenceData(REFERENCE_CACHE_KEYS.institutions, () => referenceApi.institutions({ per_page: 200 }), []),
    fetchReferenceData(REFERENCE_CACHE_KEYS.species, () => referenceApi.species({ per_page: 200 }), []),
    rolesApi.list().catch(() => []),
  ]);
}
